import { CopyOutlined, EyeOutlined, LinkOutlined, SendOutlined } from '@ant-design/icons';
import { ChannelDoc, ChannelMessage, ChannelMessageDoc, MessageCategoryConfig } from '@gooduncles/gu-app-schema';
import type { PushType } from '@gooduncles/gu-app-schema';
import { Avatar, Button, Card, Descriptions, Form, Input, List, Modal, Select, Switch, Tag, message } from 'antd';
import { FC, useEffect, useMemo, useState } from 'react';
import { map } from 'rxjs/operators';
import { useAuth } from 'src/stores/auth-context';

import { formatDate } from 'src/utils/date-util';
import {
  createNoTriggerChannelMessage,
  createTriggerChannelMessage,
  observeChannelCategoryConf,
  observeChannels,
  observeTriggerChannelMessages,
} from 'src/utils/firebase/firebase-short-cut';

import classes from './ComposeMessage.module.scss';

import MessagePreview from './MessagePreview';
import { findPushTypeOption, pushTypeOptions } from './constants';

interface FormValues {
  channelId: string;
  title: string;
  body?: string;
  type: PushType;
  category?: string;
  actionType?: string;
  actionValue?: string;
  notify: boolean;
}

const actionTypeOptions = [{ label: '페이지 이동 (navigate)', value: 'navigate' }];

interface MessageSummaryProps {
  channelName: string;
  title: string;
  body?: string | null;
  type: string;
  category?: string | null;
  categoryConfig?: MessageCategoryConfig | null;
  actionType?: string | null;
  actionValue?: string | null;
  notify?: boolean;
  createdAt?: string;
}

const MessageSummary: FC<MessageSummaryProps> = ({
  channelName,
  title,
  body,
  type,
  category,
  categoryConfig,
  actionType,
  actionValue,
  notify,
  createdAt,
}) => {
  const typeOption = findPushTypeOption(type);
  return (
    <Descriptions column={1} size='small' style={{ marginTop: 16 }} bordered>
      <Descriptions.Item label='채널'>{channelName}</Descriptions.Item>
      <Descriptions.Item label='유형'>
        <Tag color={typeOption?.color}>{typeOption?.label ?? type}</Tag>
      </Descriptions.Item>
      {category && (
        <Descriptions.Item label='카테고리'>
          {categoryConfig ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Avatar size={16} src={categoryConfig.iconUrl} />
              {categoryConfig.label}
            </span>
          ) : (
            category
          )}
        </Descriptions.Item>
      )}
      <Descriptions.Item label='제목'>{title}</Descriptions.Item>
      {body && (
        <Descriptions.Item label='내용'>
          <span style={{ whiteSpace: 'pre-wrap' }}>{body}</span>
        </Descriptions.Item>
      )}
      {actionType && (
        <Descriptions.Item label='액션'>
          {actionType} → {actionValue}
        </Descriptions.Item>
      )}
      {notify !== undefined && (
        <Descriptions.Item label='PUSH 알림'>
          <Tag color={notify ? 'green' : 'default'}>{notify ? 'ON' : 'OFF'}</Tag>
        </Descriptions.Item>
      )}
      {createdAt && (
        <Descriptions.Item label='발송일'>{formatDate(new Date(createdAt), 'yyyy-MM-dd HH:mm:ss')}</Descriptions.Item>
      )}
    </Descriptions>
  );
};

const ComposeMessage: FC = () => {
  const { user } = useAuth();
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState<ChannelDoc[]>([]);
  const [recentMessages, setRecentMessages] = useState<ChannelMessageDoc[]>([]);
  const [categoryMap, setCategoryMap] = useState<Record<string, MessageCategoryConfig>>({});

  const watchedTitle = Form.useWatch('title', form);
  const watchedBody = Form.useWatch('body', form);
  const watchedType = Form.useWatch('type', form);
  const watchedCategory = Form.useWatch('category', form);
  const watchedActionType = Form.useWatch('actionType', form);
  const watchedActionValue = Form.useWatch('actionValue', form);
  const watchedChannelId = Form.useWatch('channelId', form);

  useEffect(() => {
    const subscription = observeChannels([['mode', '==', 'broadcast']]).subscribe({
      next: (data) => setChannels(data),
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const subscription = observeChannelCategoryConf().subscribe({
      next: (doc) => setCategoryMap(doc?.categories ?? {}),
    });
    return () => subscription.unsubscribe();
  }, []);

  const broadcastChannelIds = useMemo(() => channels.map((ch) => ch._id), [channels]);

  useEffect(() => {
    if (broadcastChannelIds.length === 0) {
      setRecentMessages([]);
      return;
    }
    const subscription = observeTriggerChannelMessages([], {
      sortKey: 'createdAt',
      orderBy: 'desc',
      limit: 30,
    })
      .pipe(map((data) => data.filter((m) => broadcastChannelIds.includes(m.channelId)).slice(0, 10)))
      .subscribe({
        next: (data) => setRecentMessages(data),
      });
    return () => subscription.unsubscribe();
  }, [broadcastChannelIds]);

  const isFormValid = useMemo(() => {
    if (!watchedChannelId || !watchedTitle?.trim() || !watchedBody?.trim() || !watchedType) return false;
    if (watchedActionType && (!watchedActionValue || !/^\/.+/.test(watchedActionValue))) return false;
    return true;
  }, [watchedChannelId, watchedTitle, watchedBody, watchedType, watchedActionType, watchedActionValue]);

  const channelOptions = useMemo(
    () =>
      channels.map((ch) => ({
        label: `${ch.name} (${ch._id})`,
        value: ch._id,
      })),
    [channels]
  );

  const selectedChannelName = useMemo(
    () => channels.find((ch) => ch._id === watchedChannelId)?.name ?? '',
    [channels, watchedChannelId]
  );

  const categoryOptions = useMemo(
    () =>
      Object.entries(categoryMap).map(([key, config]) => ({
        label: (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Avatar size={16} src={config.iconUrl} />
            {config.label}
          </span>
        ),
        value: key,
      })),
    [categoryMap]
  );

  const selectedCategoryConfig = useMemo(
    () => (watchedCategory ? categoryMap[watchedCategory] ?? null : null),
    [watchedCategory, categoryMap]
  );

  useEffect(() => {
    if (!watchedActionType) {
      form.setFieldValue('actionValue', undefined);
    }
  }, [watchedActionType, form]);

  const fillFormFromMessage = (msg: ChannelMessageDoc) => {
    form.setFieldsValue({
      channelId: msg.channelId,
      title: msg.title,
      body: msg.body ?? undefined,
      type: msg.type,
      category: msg.category ?? undefined,
      actionType: msg.actionType ?? undefined,
      actionValue: msg.actionValue ?? undefined,
      notify: true,
    });
    message.info('메시지 내용이 폼에 복사되었습니다.');
  };

  const showMessageDetail = (msg: ChannelMessageDoc) => {
    Modal.info({
      title: msg.title,
      width: 480,
      content: (
        <MessageSummary
          channelName={msg.channelId}
          title={msg.title}
          body={msg.body}
          type={msg.type}
          category={msg.category}
          categoryConfig={msg.category ? categoryMap[msg.category] ?? null : null}
          actionType={msg.actionType}
          actionValue={msg.actionValue}
          createdAt={msg.createdAt}
        />
      ),
      okText: '닫기',
    });
  };

  const showConfirmAndSend = async () => {
    try {
      const values = await form.validateFields();

      if (!user) {
        message.error('로그인이 필요합니다.');
        return;
      }

      const channelName = channels.find((ch) => ch._id === values.channelId)?.name ?? values.channelId;

      Modal.confirm({
        title: '메시지를 발송하시겠습니까?',
        width: 480,
        content: (
          <MessageSummary
            channelName={channelName}
            title={values.title}
            body={values.body}
            type={values.type}
            category={values.category}
            categoryConfig={values.category ? categoryMap[values.category] ?? null : null}
            actionType={values.actionType}
            actionValue={values.actionValue}
            notify={values.notify}
          />
        ),
        okText: '발송',
        cancelText: '취소',
        onOk: () => handleSend(values),
      });
    } catch {
      // validation failed
    }
  };

  const handleSend = async (values: FormValues) => {
    if (!user) return;

    setLoading(true);
    try {
      const now = formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900");
      const messageData: ChannelMessage = {
        channelId: values.channelId,
        channelMode: 'broadcast',
        senderService: 'console',
        senderCollection: 'system',
        senderId: user._id,
        title: values.title,
        body: values.body ?? null,
        images: null,
        type: values.type,
        actionType: values.actionType ?? null,
        actionValue: values.actionValue ?? null,
        category: values.category ?? null,
        createdAt: now,
        scheduledAt: null,
        scheduleStatus: null,
        claimedAt: null,
      };

      const createFn = values.notify ? createTriggerChannelMessage : createNoTriggerChannelMessage;
      await createFn(messageData);
      message.success(
        values.notify
          ? '메시지가 발송되었습니다. FCM 푸시가 자동으로 처리됩니다.'
          : '메시지가 저장되었습니다. (PUSH 알림 없음)'
      );
      form.resetFields();
    } catch (error) {
      console.error('Failed to send message:', error);
      message.error('메시지 발송에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.container}>
      <Card title='브로드캐스트 메시지 발송'>
        <div className={classes.composeRow}>
          <div className={classes.formPanel}>
            <Form form={form} layout='vertical' requiredMark='optional'>
              <Form.Item name='channelId' label='채널' rules={[{ required: true, message: '채널을 선택해주세요.' }]}>
                <Select
                  options={channelOptions}
                  placeholder='브로드캐스트 채널 선택'
                  showSearch
                  optionFilterProp='label'
                />
              </Form.Item>

              <Form.Item
                name='type'
                label='메시지 유형'
                rules={[{ required: true, message: '유형을 선택해주세요.' }]}
                initialValue='notice'>
                <Select
                  options={pushTypeOptions.map((o) => ({
                    label: (
                      <div className={classes.typeOption}>
                        <Tag color={o.color}>{o.label}</Tag>
                      </div>
                    ),
                    value: o.value,
                  }))}
                />
              </Form.Item>

              {categoryOptions.length > 0 && (
                <Form.Item name='category' label='카테고리'>
                  <Select options={categoryOptions} placeholder='카테고리 선택 (선택)' allowClear />
                </Form.Item>
              )}

              <Form.Item name='title' label='제목' rules={[{ required: true, message: '제목을 입력해주세요.' }]}>
                <Input placeholder='알림 제목' maxLength={100} showCount />
              </Form.Item>

              <Form.Item name='body' label='내용' rules={[{ required: true, message: '내용을 입력해주세요.' }]}>
                <Input.TextArea rows={4} placeholder='알림 내용' maxLength={500} showCount />
              </Form.Item>

              <div className={classes.actionSection}>
                <div className={classes.actionHeader}>
                  <LinkOutlined />
                  액션 설정 (선택)
                </div>
                <Form.Item name='actionType' label='액션 타입' style={{ marginBottom: watchedActionType ? 12 : 0 }}>
                  <Select options={actionTypeOptions} placeholder='액션 없음' allowClear />
                </Form.Item>

                {watchedActionType && (
                  <Form.Item
                    name='actionValue'
                    label='이동 경로'
                    rules={[
                      { required: true, message: '경로를 입력해주세요.' },
                      {
                        pattern: /^\/.+/,
                        message: '/로 시작하는 경로를 입력해주세요.',
                      },
                    ]}
                    style={{ marginBottom: 0 }}>
                    <Input placeholder='/store/detail' />
                  </Form.Item>
                )}
              </div>

              <Form.Item name='notify' label='PUSH 알림' valuePropName='checked' initialValue={true}>
                <Switch checkedChildren='ON' unCheckedChildren='OFF' />
              </Form.Item>
            </Form>
          </div>

          <div className={classes.previewPanel}>
            <MessagePreview
              title={watchedTitle}
              body={watchedBody}
              type={watchedType}
              categoryLabel={selectedCategoryConfig?.label}
              categoryIconUrl={selectedCategoryConfig?.iconUrl}
              actionType={watchedActionType}
              actionValue={watchedActionValue}
              channelName={selectedChannelName}
            />
            <div className={classes.submitRow}>
              <Button
                type='primary'
                icon={<SendOutlined />}
                onClick={showConfirmAndSend}
                loading={loading}
                disabled={!isFormValid}
                size='large'
                block>
                메시지 발송
              </Button>
            </div>
          </div>

          <div className={classes.recentPanel}>
            <div className={classes.recentLabel}>최근 발송 ({recentMessages.length})</div>
            <List
              dataSource={recentMessages}
              size='small'
              locale={{ emptyText: '발송 내역 없음' }}
              renderItem={(msg) => {
                const typeOption = findPushTypeOption(msg.type);
                return (
                  <List.Item
                    className={classes.recentItem}
                    actions={[
                      <Button
                        key='copy'
                        type='text'
                        size='small'
                        icon={<CopyOutlined />}
                        onClick={() => fillFormFromMessage(msg)}
                      />,
                      <Button
                        key='detail'
                        type='text'
                        size='small'
                        icon={<EyeOutlined />}
                        onClick={() => showMessageDetail(msg)}
                      />,
                    ]}>
                    <div className={classes.recentItemContent}>
                      <div className={classes.recentItemTitle}>{msg.title}</div>
                      <div className={classes.recentItemMeta}>
                        <Tag color={typeOption?.color} style={{ marginRight: 4 }}>
                          {typeOption?.label ?? msg.type}
                        </Tag>
                        <span className={classes.recentItemDate}>
                          {formatDate(new Date(msg.createdAt), 'MM-dd')} {formatDate(new Date(msg.createdAt), 'HH:mm')}
                        </span>
                      </div>
                    </div>
                  </List.Item>
                );
              }}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ComposeMessage;
